import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import appStoreIcon from "../../../assets/images/logo/appStoreIcon.jpg";
import {
  SmartBannerContainer,
  SmartBannerInnerContainer,
  SmartBannerClose,
  SmartBannerIcon,
  SmartBannerInfo,
  SmartBannerTitle,
  SmartBannerDescription,
  SmartBannerWrapper,
  SmartBannerButton,
  SmartBannerButtonText,
  SmartBannerImage,
} from "./styledComponents";

interface ISmartBannerProps {
  title: string;
  appMeta: {
    ios?: string;
    android?: string;
  };
  deepLinks?: {
    ios?: string;
    android?: string;
  };
  position?: "top" | "bottom";
  daysHidden?: number;
  button?: string;
  storeText?: {
    ios?: string;
    android?: string;
  };
  force?: "ios" | "android";
}

export const SmartBanner = ({
  title,
  appMeta,
  deepLinks,
  position = "top",
  daysHidden = 15,
  button = "Install",
  storeText = {
    ios: "On the App Store",
    android: "In Google Play",
  },
  force,
}: ISmartBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | null>(null);
  const location = useLocation();

  const getCurrentDeepLink = () => {
    const currentPath = location.pathname + location.search + location.hash;
    const pathForApp = currentPath.startsWith("/")
      ? currentPath.substring(1)
      : currentPath;
    const finalPath = pathForApp || "";
    return `allstar://${finalPath}`;
  };

  useEffect(() => {
    const cookieKey = `smartbanner-closed-${title.toLowerCase()}`;
    const lastClosed = localStorage.getItem(cookieKey);

    if (lastClosed) {
      const daysSinceClosed =
        (Date.now() - parseInt(lastClosed)) / (1000 * 60 * 60 * 24);
      if (daysSinceClosed < daysHidden) {
        return;
      }
    }

    let detectedPlatform: "ios" | "android" | null = null;

    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);

    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/Chrome|Chromium|CriOS|FxiOS|EdgiOS/.test(navigator.userAgent) &&
      !("chrome" in window);

    if (force && !isSafari) {
      detectedPlatform = force;
    } else {
      if (isIOS && appMeta.ios && !isSafari) {
        detectedPlatform = "ios";
      } else if (isAndroid && appMeta.android) {
        detectedPlatform = "android";
      } else {
        detectedPlatform = null;
      }
    }

    if (detectedPlatform) {
      setPlatform(detectedPlatform);
      setIsVisible(true);
    }
  }, [title, daysHidden, force, appMeta, location.pathname]);

  const handleClose = () => {
    const cookieKey = `smartbanner-closed-${title.toLowerCase()}`;
    localStorage.setItem(cookieKey, Date.now().toString());
    setIsVisible(false);
  };

  const handleAppOpen = () => {
    if (!platform) return;

    const staticDeepLink = deepLinks?.[platform];
    const dynamicDeepLink = getCurrentDeepLink();
    const deepLink = dynamicDeepLink || staticDeepLink;

    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/Chrome|Chromium|CriOS|FxiOS|EdgiOS/.test(navigator.userAgent) &&
      !("chrome" in window);

    if (platform === "ios" && isSafari) {
      window.open(getStoreUrl(), "_blank");
      return;
    }

    if (deepLink) {
      const appOpenStart = Date.now();

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.style.position = "absolute";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.top = "-9999px";
      iframe.src = deepLink;
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      setTimeout(cleanup, 100);

      const checkAppOpened = () => {
        const timeElapsed = Date.now() - appOpenStart;

        if (timeElapsed > 2500 && document.visibilityState === "visible") {
          window.open(getStoreUrl(), "_blank");
        }
      };

      if (platform === "ios") {
        setTimeout(checkAppOpened, 2500);
      } else {
        const handleVisibilityChange = () => {
          if (document.visibilityState === "hidden") {
            document.removeEventListener(
              "visibilitychange",
              handleVisibilityChange,
            );
          }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        setTimeout(() => {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
          checkAppOpened();
        }, 2500);
      }
    } else {
      window.open(getStoreUrl(), "_blank");
    }
  };

  const getStoreUrl = () => {
    if (platform === "ios" && appMeta.ios) {
      return `https://apps.apple.com/app/id${appMeta.ios}`;
    } else if (platform === "android" && appMeta.android) {
      return `https://play.google.com/store/apps/details?id=${appMeta.android}`;
    }
    return "#";
  };

  if (!isVisible || !platform) {
    return null;
  }

  return (
    <SmartBannerContainer $platform={platform} $position={position}>
      <SmartBannerInnerContainer>
        <SmartBannerClose $platform={platform} onClick={handleClose}>
          ×
        </SmartBannerClose>
        <SmartBannerIcon $platform={platform}>
          <SmartBannerImage src={appStoreIcon} alt={`${platform} store icon`} />
        </SmartBannerIcon>
        <SmartBannerInfo $platform={platform}>
          <SmartBannerTitle $platform={platform}>{title}</SmartBannerTitle>
          <SmartBannerDescription>{storeText[platform]}</SmartBannerDescription>
        </SmartBannerInfo>
        <SmartBannerWrapper>
          <SmartBannerButton
            classification="smartBanner"
            $platform={platform}
            onClick={handleAppOpen}
          >
            {platform === "android" ? (
              <SmartBannerButtonText>{button}</SmartBannerButtonText>
            ) : (
              button
            )}
          </SmartBannerButton>
        </SmartBannerWrapper>
      </SmartBannerInnerContainer>
    </SmartBannerContainer>
  );
};
